import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  serviceLocationsAPI,
  allServicesAPI,
  serviceProfileAPI,
  holidayUpliftAPI,
} from "../../services/api";

// ==================== INITIAL STATE ====================
const getInitialState = () => ({
  // ── List page ──────────────────────────────────────────────────────────
  bases: [],
  summary: { TotalLocations: 0, ActiveServicesCount: 0, InCatalogueCount: 0 },
  allServices: [],

  // ── ServiceDetailsScreen ───────────────────────────────────────────────
  currentBase: null,

  // ── ServiceSettings ────────────────────────────────────────────────────
  currentServiceVariants: null,
  currentServiceAvailability: null,
  currentServiceProfile: null,
  serviceProfileImages: [],

  // ── Holiday Uplift ─────────────────────────────────────────────────────
  holidayUplift: null, // full GET response
  isSavingHoliday: false,
  isLoadingHoliday: false,
  holidayError: null,

  // ── Loading flags ──────────────────────────────────────────────────────
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isLoadingServices: false,
  isLoadingDetail: false,
  isTogglingService: false,
  isLoadingVariants: false,
  isLoadingAvailability: false,
  isSavingVariants: false,
  isSavingAvailability: false,
  isLoadingProfile: false,
  isSavingProfile: false,
  isUploadingImage: false,
  isDeletingImage: false,

  // ── Errors ────────────────────────────────────────────────────────────
  error: null,
  variantsError: null,
  availabilityError: null,
  profileError: null,
});

// ── Debug helpers ──────────────────────────────────────────────────────────
const logOk = (label, data) =>
  console.log(`%c✅ [${label}]`, "color:#10b981;font-weight:700", data);
const logErr = (label, error) =>
  console.error(`%c❌ [${label}]`, "color:#ef4444;font-weight:700", error);
const logOut = (label, data) =>
  console.log(`%c📤 [${label}] payload`, "color:#6366f1;font-weight:700", data);

// ==================== EXISTING THUNKS (unchanged) ====================

export const getBaseLocations = createAsyncThunk(
  "serviceLocations/getBaseLocations",
  async (_, { rejectWithValue }) => {
    try {
      const res = await serviceLocationsAPI.getBaseLocations();
      logOk("getBaseLocations", res.data);
      return res.data;
    } catch (e) {
      logErr("getBaseLocations", e.response?.data ?? e.message);
      return rejectWithValue(
        e.response?.data?.Error ||
          e.response?.data?.message ||
          "Failed to fetch base locations.",
      );
    }
  },
);

export const getBaseLocationDetail = createAsyncThunk(
  "serviceLocations/getBaseLocationDetail",
  async (baseId, { rejectWithValue }) => {
    try {
      const res = await serviceLocationsAPI.getBaseLocationDetail(baseId);
      logOk("getBaseLocationDetail", res.data);
      return res.data;
    } catch (e) {
      logErr("getBaseLocationDetail", e.response?.data ?? e.message);
      return rejectWithValue(
        e.response?.data?.Error ||
          e.response?.data?.message ||
          "Failed to fetch location details.",
      );
    }
  },
);

export const toggleBaseService = createAsyncThunk(
  "serviceLocations/toggleBaseService",
  async ({ baseId, serviceId, isEnabled }, { rejectWithValue }) => {
    try {
      const res = await serviceLocationsAPI.toggleBaseService(
        baseId,
        serviceId,
        { IsEnabled: isEnabled },
      );
      logOk("toggleBaseService", res.data);
      return res.data;
    } catch (e) {
      logErr("toggleBaseService", e.response?.data ?? e.message);
      return rejectWithValue(
        e.response?.data?.Error ||
          e.response?.data?.message ||
          "Failed to toggle service.",
      );
    }
  },
);

export const createBaseLocation = createAsyncThunk(
  "serviceLocations/createBaseLocation",
  async (data, { rejectWithValue }) => {
    logOut("createBaseLocation", data);
    try {
      const res = await serviceLocationsAPI.createBaseLocation(data);
      logOk("createBaseLocation", res.data);
      return res.data;
    } catch (e) {
      logErr("createBaseLocation", e.response?.data ?? e.message);
      return rejectWithValue(
        e.response?.data?.Error ||
          e.response?.data?.message ||
          "Failed to create base location.",
      );
    }
  },
);

export const updateBaseLocation = createAsyncThunk(
  "serviceLocations/updateBaseLocation",
  async ({ baseId, data }, { rejectWithValue }) => {
    logOut("updateBaseLocation", { baseId, data });
    try {
      const res = await serviceLocationsAPI.updateBaseLocation(baseId, data);
      logOk("updateBaseLocation", res.data);
      return res.data;
    } catch (e) {
      logErr("updateBaseLocation", e.response?.data ?? e.message);
      return rejectWithValue(
        e.response?.data?.Error ||
          e.response?.data?.message ||
          "Failed to update base location.",
      );
    }
  },
);

export const deleteBaseLocation = createAsyncThunk(
  "serviceLocations/deleteBaseLocation",
  async (baseId, { rejectWithValue }) => {
    try {
      await serviceLocationsAPI.deleteBaseLocation(baseId);
      logOk("deleteBaseLocation", { baseId });
      return { baseId };
    } catch (e) {
      logErr("deleteBaseLocation", e.response?.data ?? e.message);
      return rejectWithValue(
        e.response?.data?.Error ||
          e.response?.data?.message ||
          "Failed to delete base location.",
      );
    }
  },
);

export const getAllServices = createAsyncThunk(
  "serviceLocations/getAllServices",
  async (_, { rejectWithValue }) => {
    try {
      const res = await allServicesAPI.getAllServices();
      logOk("getAllServices", res.data);
      return res.data;
    } catch (e) {
      logErr("getAllServices", e.response?.data ?? e.message);
      return rejectWithValue(
        e.response?.data?.Error ||
          e.response?.data?.message ||
          "Failed to fetch services catalogue.",
      );
    }
  },
);

// ==================== SERVICE SETTINGS THUNKS ====================

export const getServiceVariants = createAsyncThunk(
  "serviceLocations/getServiceVariants",
  async ({ baseId, serviceId }, { rejectWithValue }) => {
    try {
      const res = await serviceLocationsAPI.getServiceVariants(
        baseId,
        serviceId,
      );
      logOk("getServiceVariants", res.data);
      return res.data;
    } catch (e) {
      logErr("getServiceVariants", e.response?.data ?? e.message);
      return rejectWithValue(
        e.response?.data?.Error ||
          e.response?.data?.message ||
          "Failed to fetch variants.",
      );
    }
  },
);

export const updateServiceVariants = createAsyncThunk(
  "serviceLocations/updateServiceVariants",
  async ({ baseId, serviceId, data }, { rejectWithValue }) => {
    logOut("updateServiceVariants", { baseId, serviceId, data });
    try {
      const res = await serviceLocationsAPI.updateServiceVariants(
        baseId,
        serviceId,
        data,
      );
      logOk("updateServiceVariants", res.data);
      return res.data;
    } catch (e) {
      logErr("updateServiceVariants", e.response?.data ?? e.message);
      return rejectWithValue(
        e.response?.data?.Error ||
          e.response?.data?.message ||
          "Failed to update variants.",
      );
    }
  },
);

export const getServiceAvailability = createAsyncThunk(
  "serviceLocations/getServiceAvailability",
  async ({ baseId, serviceId }, { rejectWithValue }) => {
    try {
      const res = await serviceLocationsAPI.getServiceAvailability(
        baseId,
        serviceId,
      );
      logOk("getServiceAvailability", res.data);
      return res.data;
    } catch (e) {
      logErr("getServiceAvailability", e.response?.data ?? e.message);
      return rejectWithValue(
        e.response?.data?.Error ||
          e.response?.data?.message ||
          "Failed to fetch availability.",
      );
    }
  },
);

export const saveServiceAvailability = createAsyncThunk(
  "serviceLocations/saveServiceAvailability",
  async ({ baseId, serviceId, data }, { rejectWithValue }) => {
    logOut("saveServiceAvailability", { baseId, serviceId, data });
    try {
      const res = await serviceLocationsAPI.saveServiceAvailability(
        baseId,
        serviceId,
        data,
      );
      logOk("saveServiceAvailability", res.data);
      return res.data;
    } catch (e) {
      logErr("saveServiceAvailability", e.response?.data ?? e.message);
      return rejectWithValue(
        e.response?.data?.Error ||
          e.response?.data?.message ||
          "Failed to save availability.",
      );
    }
  },
);

export const addServiceAvailabilitySlot = createAsyncThunk(
  "serviceLocations/addServiceAvailabilitySlot",
  async ({ baseId, serviceId, data }, { rejectWithValue }) => {
    logOut("addServiceAvailabilitySlot", { baseId, serviceId, data });
    try {
      const res = await serviceLocationsAPI.addServiceAvailabilitySlot(
        baseId,
        serviceId,
        data,
      );
      logOk("addServiceAvailabilitySlot", res.data);
      return res.data;
    } catch (e) {
      logErr("addServiceAvailabilitySlot", e.response?.data ?? e.message);
      return rejectWithValue(
        e.response?.data?.Error ||
          e.response?.data?.message ||
          "Failed to add slot.",
      );
    }
  },
);

// ==================== SERVICE PROFILE THUNKS ====================

export const getServiceProfile = createAsyncThunk(
  "serviceLocations/getServiceProfile",
  async (serviceId, { rejectWithValue }) => {
    try {
      const res = await serviceProfileAPI.getProfile(serviceId);
      logOk("getServiceProfile", res.data);
      return res.data;
    } catch (e) {
      logErr("getServiceProfile", e.response?.data ?? e.message);
      return rejectWithValue(
        e.response?.data?.Error ||
          e.response?.data?.message ||
          "Failed to fetch service profile.",
      );
    }
  },
);

export const updateServiceProfile = createAsyncThunk(
  "serviceLocations/updateServiceProfile",
  async ({ serviceId, data }, { rejectWithValue }) => {
    logOut("updateServiceProfile", { serviceId, data });
    try {
      const res = await serviceProfileAPI.updateProfile(serviceId, data);
      logOk("updateServiceProfile", res.data);
      return res.data;
    } catch (e) {
      logErr("updateServiceProfile", e.response?.data ?? e.message);
      return rejectWithValue(
        e.response?.data?.Error ||
          e.response?.data?.message ||
          "Failed to update service profile.",
      );
    }
  },
);

export const getServiceProfileImages = createAsyncThunk(
  "serviceLocations/getServiceProfileImages",
  async (serviceId, { rejectWithValue }) => {
    try {
      const res = await serviceProfileAPI.getImages(serviceId);
      logOk("getServiceProfileImages", res.data);
      return res.data;
    } catch (e) {
      logErr("getServiceProfileImages", e.response?.data ?? e.message);
      return rejectWithValue(
        e.response?.data?.Error ||
          e.response?.data?.message ||
          "Failed to fetch images.",
      );
    }
  },
);

export const uploadServiceProfileImage = createAsyncThunk(
  "serviceLocations/uploadServiceProfileImage",
  async ({ serviceId, formData }, { rejectWithValue }) => {
    try {
      const res = await serviceProfileAPI.uploadImage(serviceId, formData);
      logOk("uploadServiceProfileImage", res.data);
      return res.data;
    } catch (e) {
      logErr("uploadServiceProfileImage", e.response?.data ?? e.message);
      return rejectWithValue(
        e.response?.data?.Error ||
          e.response?.data?.message ||
          "Failed to upload image.",
      );
    }
  },
);

export const deleteServiceProfileImage = createAsyncThunk(
  "serviceLocations/deleteServiceProfileImage",
  async ({ serviceId, imageId }, { rejectWithValue }) => {
    try {
      await serviceProfileAPI.deleteImage(serviceId, imageId);
      logOk("deleteServiceProfileImage", { imageId });
      return { imageId };
    } catch (e) {
      logErr("deleteServiceProfileImage", e.response?.data ?? e.message);
      return rejectWithValue(
        e.response?.data?.Error ||
          e.response?.data?.message ||
          "Failed to delete image.",
      );
    }
  },
);

// ==================== HOLIDAY UPLIFT THUNKS (NEW) ====================

// GET /HolidayUplift/:serviceId/
export const getHolidayUplift = createAsyncThunk(
  "serviceLocations/getHolidayUplift",
  async (serviceId, { rejectWithValue }) => {
    try {
      const res = await holidayUpliftAPI.getHolidayUplift(serviceId);
      logOk("getHolidayUplift", res.data);
      return res.data;
    } catch (e) {
      logErr("getHolidayUplift", e.response?.data ?? e.message);
      return rejectWithValue(
        e.response?.data?.Error ||
          e.response?.data?.message ||
          "Failed to fetch holiday uplift.",
      );
    }
  },
);

// POST /HolidayUplift/:serviceId/ — saves ONE holiday at a time
// Called in a loop from the component for all modified holidays
export const saveHolidayUplift = createAsyncThunk(
  "serviceLocations/saveHolidayUplift",
  async ({ serviceId, data }, { rejectWithValue }) => {
    logOut("saveHolidayUplift", { serviceId, data });
    try {
      const res = await holidayUpliftAPI.saveHolidayUplift(serviceId, data);
      logOk("saveHolidayUplift", res.data);
      return res.data;
    } catch (e) {
      logErr("saveHolidayUplift", e.response?.data ?? e.message);
      return rejectWithValue(
        e.response?.data?.Error ||
          e.response?.data?.message ||
          "Failed to save holiday uplift.",
      );
    }
  },
);

// ==================== SLICE ====================
const serviceLocationsSlice = createSlice({
  name: "serviceLocations",
  initialState: getInitialState(),
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentBase: (state) => {
      state.currentBase = null;
    },
    clearServiceSettings: (state) => {
      state.currentServiceVariants = null;
      state.currentServiceAvailability = null;
      state.currentServiceProfile = null;
      state.serviceProfileImages = [];
      state.holidayUplift = null;
      state.variantsError = null;
      state.availabilityError = null;
      state.profileError = null;
      state.holidayError = null;
    },
  },
  extraReducers: (builder) => {
    // ── GET BASE LOCATIONS ────────────────────────────────────────────────
    builder
      .addCase(getBaseLocations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getBaseLocations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bases = action.payload.Bases || [];
        state.summary = {
          TotalLocations: action.payload.TotalLocations ?? 0,
          ActiveServicesCount: action.payload.ActiveServicesCount ?? 0,
          InCatalogueCount: action.payload.InCatalogueCount ?? 0,
        };
        state.error = null;
      })
      .addCase(getBaseLocations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // ── GET BASE LOCATION DETAIL ──────────────────────────────────────────
    builder
      .addCase(getBaseLocationDetail.pending, (state) => {
        state.isLoadingDetail = true;
        state.currentBase = null;
        state.error = null;
      })
      .addCase(getBaseLocationDetail.fulfilled, (state, action) => {
        state.isLoadingDetail = false;
        state.currentBase = {
          ...(action.payload.Base || action.payload),
          AvailableCount: action.payload.AvailableCount ?? 0,
          ActiveCount: action.payload.ActiveCount ?? 0,
        };
        state.error = null;
      })
      .addCase(getBaseLocationDetail.rejected, (state, action) => {
        state.isLoadingDetail = false;
        state.error = action.payload;
      });

    // ── TOGGLE SERVICE ────────────────────────────────────────────────────
    builder
      .addCase(toggleBaseService.pending, (state) => {
        state.isTogglingService = true;
        state.error = null;
      })
      .addCase(toggleBaseService.fulfilled, (state, action) => {
        state.isTogglingService = false;
        const svc = action.payload.Service;
        if (svc && state.currentBase?.Services) {
          const idx = state.currentBase.Services.findIndex(
            (s) => s.ServiceId === svc.ServiceId,
          );
          if (idx !== -1)
            state.currentBase.Services[idx] = {
              ...state.currentBase.Services[idx],
              IsEnabled: svc.IsEnabled,
            };
        }
        state.error = null;
      })
      .addCase(toggleBaseService.rejected, (state, action) => {
        state.isTogglingService = false;
        state.error = action.payload;
      });

    // ── CREATE BASE LOCATION ──────────────────────────────────────────────
    builder
      .addCase(createBaseLocation.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createBaseLocation.fulfilled, (state, action) => {
        state.isCreating = false;
        if (action.payload.Base) {
          state.bases.unshift(action.payload.Base);
          state.summary.TotalLocations += 1;
        }
        state.error = null;
      })
      .addCase(createBaseLocation.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload;
      });

    // ── UPDATE BASE LOCATION ──────────────────────────────────────────────
    builder
      .addCase(updateBaseLocation.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateBaseLocation.fulfilled, (state, action) => {
        state.isUpdating = false;
        if (action.payload.Base) {
          const u = action.payload.Base;
          const idx = state.bases.findIndex((b) => b.BaseId === u.BaseId);
          if (idx !== -1) state.bases[idx] = u;
          if (state.currentBase?.BaseId === u.BaseId) state.currentBase = u;
        }
        state.error = null;
      })
      .addCase(updateBaseLocation.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      });

    // ── DELETE BASE LOCATION ──────────────────────────────────────────────
    builder
      .addCase(deleteBaseLocation.pending, (state) => {
        state.isUpdating = true;
      })
      .addCase(deleteBaseLocation.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.bases = state.bases.filter(
          (b) => b.BaseId !== action.payload.baseId,
        );
        state.summary.TotalLocations = Math.max(
          0,
          state.summary.TotalLocations - 1,
        );
      })
      .addCase(deleteBaseLocation.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      });

    // ── GET ALL SERVICES ──────────────────────────────────────────────────
    builder
      .addCase(getAllServices.pending, (state) => {
        state.isLoadingServices = true;
      })
      .addCase(getAllServices.fulfilled, (state, action) => {
        state.isLoadingServices = false;
        state.allServices = action.payload.Services || [];
      })
      .addCase(getAllServices.rejected, (state, action) => {
        state.isLoadingServices = false;
        state.error = action.payload;
      });

    // ── GET SERVICE VARIANTS ──────────────────────────────────────────────
    builder
      .addCase(getServiceVariants.pending, (state) => {
        state.isLoadingVariants = true;
        state.variantsError = null;
      })
      .addCase(getServiceVariants.fulfilled, (state, action) => {
        state.isLoadingVariants = false;
        state.currentServiceVariants = action.payload;
      })
      .addCase(getServiceVariants.rejected, (state, action) => {
        state.isLoadingVariants = false;
        state.variantsError = action.payload;
      });

    // ── UPDATE SERVICE VARIANTS ───────────────────────────────────────────
    builder
      .addCase(updateServiceVariants.pending, (state) => {
        state.isSavingVariants = true;
        state.variantsError = null;
      })
      .addCase(updateServiceVariants.fulfilled, (state, action) => {
        state.isSavingVariants = false;
        if (action.payload.Variants && state.currentServiceVariants) {
          state.currentServiceVariants = {
            ...state.currentServiceVariants,
            Variants: action.payload.Variants,
            RadiusMiles: action.payload.RadiusMiles,
          };
        }
      })
      .addCase(updateServiceVariants.rejected, (state, action) => {
        state.isSavingVariants = false;
        state.variantsError = action.payload;
      });

    // ── GET SERVICE AVAILABILITY ──────────────────────────────────────────
    builder
      .addCase(getServiceAvailability.pending, (state) => {
        state.isLoadingAvailability = true;
        state.availabilityError = null;
      })
      .addCase(getServiceAvailability.fulfilled, (state, action) => {
        state.isLoadingAvailability = false;
        state.currentServiceAvailability = action.payload;
      })
      .addCase(getServiceAvailability.rejected, (state, action) => {
        state.isLoadingAvailability = false;
        state.availabilityError = action.payload;
      });

    // ── SAVE SERVICE AVAILABILITY ─────────────────────────────────────────
    builder
      .addCase(saveServiceAvailability.pending, (state) => {
        state.isSavingAvailability = true;
        state.availabilityError = null;
      })
      .addCase(saveServiceAvailability.fulfilled, (state) => {
        state.isSavingAvailability = false;
      })
      .addCase(saveServiceAvailability.rejected, (state, action) => {
        state.isSavingAvailability = false;
        state.availabilityError = action.payload;
      });

    // ── ADD AVAILABILITY SLOT (kept in slice, not used in SAVE flow) ──────
    builder
      .addCase(addServiceAvailabilitySlot.pending, (state) => {
        state.isSavingAvailability = true;
        state.availabilityError = null;
      })
      .addCase(addServiceAvailabilitySlot.fulfilled, (state) => {
        state.isSavingAvailability = false;
      })
      .addCase(addServiceAvailabilitySlot.rejected, (state, action) => {
        state.isSavingAvailability = false;
        state.availabilityError = action.payload;
      });

    // ── GET SERVICE PROFILE ───────────────────────────────────────────────
    builder
      .addCase(getServiceProfile.pending, (state) => {
        state.isLoadingProfile = true;
        state.profileError = null;
      })
      .addCase(getServiceProfile.fulfilled, (state, action) => {
        state.isLoadingProfile = false;
        state.currentServiceProfile = action.payload.Profile || action.payload;
      })
      .addCase(getServiceProfile.rejected, (state, action) => {
        state.isLoadingProfile = false;
        state.profileError = action.payload;
      });

    // ── UPDATE SERVICE PROFILE ────────────────────────────────────────────
    builder
      .addCase(updateServiceProfile.pending, (state) => {
        state.isSavingProfile = true;
        state.profileError = null;
      })
      .addCase(updateServiceProfile.fulfilled, (state, action) => {
        state.isSavingProfile = false;
        state.currentServiceProfile =
          action.payload.Profile || state.currentServiceProfile;
      })
      .addCase(updateServiceProfile.rejected, (state, action) => {
        state.isSavingProfile = false;
        state.profileError = action.payload;
      });

    // ── GET SERVICE PROFILE IMAGES ────────────────────────────────────────
    builder
      .addCase(getServiceProfileImages.pending, (state) => {
        state.isUploadingImage = false;
      })
      .addCase(getServiceProfileImages.fulfilled, (state, action) => {
        state.serviceProfileImages = action.payload.Images || [];
      })
      .addCase(getServiceProfileImages.rejected, (state, action) => {
        state.profileError = action.payload;
      });

    // ── UPLOAD SERVICE PROFILE IMAGE ──────────────────────────────────────
    builder
      .addCase(uploadServiceProfileImage.pending, (state) => {
        state.isUploadingImage = true;
        state.profileError = null;
      })
      .addCase(uploadServiceProfileImage.fulfilled, (state, action) => {
        state.isUploadingImage = false;
        if (action.payload.Image)
          state.serviceProfileImages.push(action.payload.Image);
      })
      .addCase(uploadServiceProfileImage.rejected, (state, action) => {
        state.isUploadingImage = false;
        state.profileError = action.payload;
      });

    // ── DELETE SERVICE PROFILE IMAGE ──────────────────────────────────────
    builder
      .addCase(deleteServiceProfileImage.pending, (state) => {
        state.isDeletingImage = true;
        state.profileError = null;
      })
      .addCase(deleteServiceProfileImage.fulfilled, (state, action) => {
        state.isDeletingImage = false;
        state.serviceProfileImages = state.serviceProfileImages.filter(
          (img) => img.Id !== action.payload.imageId,
        );
      })
      .addCase(deleteServiceProfileImage.rejected, (state, action) => {
        state.isDeletingImage = false;
        state.profileError = action.payload;
      });

    // ── GET HOLIDAY UPLIFT (NEW) ──────────────────────────────────────────
    builder
      .addCase(getHolidayUplift.pending, (state) => {
        state.isLoadingHoliday = true;
        state.holidayError = null;
      })
      .addCase(getHolidayUplift.fulfilled, (state, action) => {
        state.isLoadingHoliday = false;
        state.holidayUplift = action.payload;
      })
      .addCase(getHolidayUplift.rejected, (state, action) => {
        state.isLoadingHoliday = false;
        state.holidayError = action.payload;
      });

    // ── SAVE HOLIDAY UPLIFT (NEW) — fires once per holiday in a loop ──────
    builder
      .addCase(saveHolidayUplift.pending, (state) => {
        state.isSavingHoliday = true;
        state.holidayError = null;
      })
      .addCase(saveHolidayUplift.fulfilled, (state) => {
        state.isSavingHoliday = false;
      })
      .addCase(saveHolidayUplift.rejected, (state, action) => {
        state.isSavingHoliday = false;
        state.holidayError = action.payload;
      });
  },
});

export const { clearError, clearCurrentBase, clearServiceSettings } =
  serviceLocationsSlice.actions;
export default serviceLocationsSlice.reducer;
